import type { QuizForInstance, SubmitQuizResponse } from "@repo/shared/types";
import { useState } from "react";
import { toast } from "sonner";
import { useAnsweringSubjects } from "@/lib/hooks/use-flow-instances/use-answering-subjects";
import {
  useInstanceQuizzes,
  useOverrideQuiz,
  useStartQuiz,
  useSubmitQuiz,
} from "@/lib/hooks/use-flow-instances/use-quiz";
import { useT } from "@/lib/i18n/use-t";
import { QuizModal } from "../quiz-modal";
import { QuizOverrideDialog } from "../quiz-override-dialog";
import { QuizWidget } from "../quiz-widget";
import { T } from "./wizard-quiz.i18n";

type WizardQuizProps = {
  instanceId: string;
  /** The flow component's id — which IS the quiz id (QL-13). */
  componentId: string;
  disabled: boolean;
  config: {
    style?: "none" | "border" | "box";
    title?: string;
    description?: string;
  };
  language: string;
  hasValidationError?: boolean;
};

/**
 * Advisor surface for one `quiz` component. The quiz is authored in this
 * component's own config, so the component id identifies it in the instance's
 * standings. Runs an attempt in the shared modal and — unlike the public
 * surface — can override a quiz the client has failed out of (per member, for
 * a group without a representative).
 */
export function WizardQuiz({
  instanceId,
  componentId,
  disabled,
  config,
  language,
  hasValidationError,
}: WizardQuizProps) {
  const t = useT(T);
  const { quizzes, isLoading } = useInstanceQuizzes(instanceId);
  const { attribution, isLoading: attributionLoading } = useAnsweringSubjects(instanceId);
  const startQuiz = useStartQuiz(instanceId);
  const submitQuiz = useSubmitQuiz(instanceId);
  const overrideQuiz = useOverrideQuiz(instanceId);

  const [attempt, setAttempt] = useState<Awaited<ReturnType<typeof startQuiz.mutateAsync>> | null>(
    null,
  );
  // QL-505 — which member the in-flight attempt / override dialog is for.
  // `undefined` for every client but a group without a representative.
  const [attemptSubjectId, setAttemptSubjectId] = useState<string | undefined>(undefined);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideSubjectId, setOverrideSubjectId] = useState<string | undefined>(undefined);

  // Wait for the attribution too, so a members-mode group never flashes the
  // single-subject layout before its member lines arrive.
  if (isLoading || attributionLoading) return null;

  // The endpoint returns every quiz on the flow; this component is one of them,
  // and is absent when it has no questions yet.
  const standing: QuizForInstance | undefined = quizzes?.find(
    (entry) => entry.quiz.id === componentId,
  );

  const handleStart = async (subjectId?: string) => {
    try {
      const result = await startQuiz.mutateAsync({ quizId: componentId, subjectId });
      setAttemptSubjectId(subjectId);
      setAttempt(result);
    } catch {
      toast.error(t.startFailed);
    }
  };

  const handleSubmit = async (
    answers: Array<{ questionId: string; selectedOptionId: string }>,
  ): Promise<SubmitQuizResponse> =>
    submitQuiz.mutateAsync({ quizId: componentId, answers, subjectId: attemptSubjectId });

  const handleOverride = (reason: string) => {
    overrideQuiz.mutate(
      { quizId: componentId, reason, subjectId: overrideSubjectId },
      {
        onSuccess: () => {
          setOverrideOpen(false);
          toast.success(t.overridden);
        },
        onError: () => toast.error(t.overrideFailed),
      },
    );
  };

  const openOverride = (subjectId?: string) => {
    setOverrideSubjectId(subjectId);
    setOverrideOpen(true);
  };

  return (
    <div className="space-y-2">
      <QuizWidget
        standing={standing}
        onStart={handleStart}
        isStarting={startQuiz.isPending}
        disabled={disabled}
        style={config.style}
        title={config.title}
        description={config.description}
        language={language}
        hasValidationError={hasValidationError}
        onOverride={disabled ? undefined : (subjectId) => openOverride(subjectId)}
        overrideByName={standing?.override?.byUserName ?? null}
        overrideReason={standing?.override?.reason ?? null}
        attribution={attribution ?? undefined}
      />

      {attempt && standing && (
        <QuizModal
          quizName={standing.quiz.name}
          attempt={attempt}
          onSubmit={handleSubmit}
          onClose={() => setAttempt(null)}
        />
      )}

      {standing && (
        <QuizOverrideDialog
          open={overrideOpen}
          onOpenChange={setOverrideOpen}
          quizName={standing.quiz.name}
          onConfirm={handleOverride}
          isSaving={overrideQuiz.isPending}
        />
      )}
    </div>
  );
}
